import { getRegistration, getUpdateSW, onNeedRefresh } from "@/registerSW";
import { isOnSessionPage } from "@/utils/route";

const UPDATE_CHECK_INTERVAL = 2 * 60 * 1000; // 2 minutes
const RETRY_DELAY = 5000; // 5 seconds
const MAX_RETRIES = 3;
const AUTO_APPLY_CHECK_INTERVAL = 60 * 1000; // 1 minute

export type UpdateStatus = {
  hasUpdate: boolean;
  isUpdating: boolean;
  lastChecked: Date | null;
};

export class SimpleUpdateManager {
  private checkInterval: number | null = null;
  private autoApplyInterval: number | null = null;
  private listeners: ((status: UpdateStatus) => void)[] = [];
  private status: UpdateStatus = {
    hasUpdate: false,
    isUpdating: false,
    lastChecked: null,
  };
  private retryCount = 0;

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === "visible") {
      void this.checkForUpdates();
    }
  };

  private handleFocus = (): void => {
    void this.checkForUpdates();
  };

  constructor() {
    // Subscribe to vite-plugin-pwa's onNeedRefresh event.
    // Fires when a new SW is installed and waiting to activate.
    onNeedRefresh(() => {
      console.log("🔄 Update available!");
      this.status = {
        ...this.status,
        hasUpdate: true,
        lastChecked: new Date(),
      };
      this.notifyListeners();
    });
  }

  startPolling(): void {
    this.startPeriodicCheck();
    this.setupAutoApply();
  }

  // Auto-applies a waiting update as soon as the kiosk isn't mid-session —
  // an unattended kiosk has no one to click the manual update prompt.
  private setupAutoApply(): void {
    if (this.autoApplyInterval) {
      return;
    }
    this.autoApplyInterval = window.setInterval(() => {
      if (
        this.status.hasUpdate &&
        !this.status.isUpdating &&
        !isOnSessionPage()
      ) {
        void this.applyUpdate();
      }
    }, AUTO_APPLY_CHECK_INTERVAL);
  }

  // useSyncExternalStore compatible API
  subscribe = (callback: () => void): (() => void) => {
    const listener = () => callback();
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  };

  getSnapshot = (): UpdateStatus => {
    return this.status;
  };

  private startPeriodicCheck(): void {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    // Delay to let SW register first
    setTimeout(() => {
      this.checkInterval = window.setInterval(
        () => this.checkForUpdates(),
        UPDATE_CHECK_INTERVAL
      );

      // Initial check
      this.checkForUpdates();
    }, 2000);

    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("focus", this.handleFocus);
  }

  async checkForUpdates(): Promise<boolean> {
    try {
      const registration = getRegistration();
      if (!registration) {
        return false;
      }

      // If there's already a waiting SW, ensure we reflect that
      if (registration.waiting) {
        if (!this.status.hasUpdate) {
          this.status = {
            ...this.status,
            hasUpdate: true,
            lastChecked: new Date(),
          };
          this.notifyListeners();
        }
        this.retryCount = 0;
        return true;
      }

      // Trigger SW update check.
      // If a new SW is found and installs, onNeedRefresh fires and sets hasUpdate.
      try {
        await registration.update();
        this.retryCount = 0;
      } catch {
        if (this.retryCount < MAX_RETRIES) {
          this.retryCount++;
          console.log(
            `⚠️ Update check failed, retrying (${this.retryCount}/${MAX_RETRIES})...`
          );
          setTimeout(() => this.checkForUpdates(), RETRY_DELAY);
          return false;
        }
        console.warn("❌ Update check failed after max retries");
        this.retryCount = 0;
      }

      this.status = { ...this.status, lastChecked: new Date() };
      this.notifyListeners();

      return this.status.hasUpdate;
    } catch (error) {
      console.error("❌ Failed to check for updates:", error);
      return false;
    }
  }

  async applyUpdate(): Promise<void> {
    const updateSW = getUpdateSW();
    if (!updateSW) {
      console.warn("⚠️ No updateSW function available");
      return;
    }

    this.status = { ...this.status, isUpdating: true };
    this.notifyListeners();

    try {
      // vite-plugin-pwa handles: SKIP_WAITING -> controllerchange -> reload
      await updateSW(true);
    } catch (error) {
      console.error("❌ Failed to apply update:", error);
      this.status = { ...this.status, isUpdating: false, hasUpdate: false };
      this.notifyListeners();
    }
  }

  addListener(listener: (status: UpdateStatus) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.status));
  }

  getStatus(): UpdateStatus {
    return { ...this.status };
  }

  destroy(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    if (this.autoApplyInterval) {
      clearInterval(this.autoApplyInterval);
      this.autoApplyInterval = null;
    }
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange
    );
    window.removeEventListener("focus", this.handleFocus);
  }
}

export const simpleUpdateManager = new SimpleUpdateManager();
